"use client";

import { useEffect, useState } from "react";
import type { NearestOfficeCollectionItem } from "@/lib/utils/facility-collection-shape";
import { getClosestFacility } from "@/lib/utils/find-nearby-facilities";
import { calculateDistance } from "@/lib/utils/calculate-distance";
import { smileApiPath } from "./smileApiPath";

/**
 * ONE source of truth for "where is this visitor / which office is nearest"
 * on the /smile chrome. Consumed by SmileNearestOfficeEnhancer, which
 * progressively upgrades the entry-authored band's "Find an office" link;
 * SmileMobileHeader mirrors that same anchor via MutationObserver, so the
 * compact chrome can never disagree with the band.
 *
 * Layered resolution ladder — the whole point is that the capability works
 * with ZERO user permission, the way production aspendental.com does (its
 * CloudFront edge injects cloudfront-viewer-latitude/longitude and the server
 * resolves the office; verified live 8/24 — the RSC payload's userCoordinates
 * byte-match the response's cloudfront-viewer-* headers, and none of its 29
 * chunks contain getCurrentPosition):
 *
 *   1. cached last-known fix (localStorage, 24h TTL) — instant paint
 *   2. precise browser geolocation ONLY when permissions.query reports
 *      "granted" — the header must NEVER trigger a permission prompt
 *   3. IP-based approximate fix from our own /api/geo/ route (no permission,
 *      city-level accuracy; the route prefers edge geo headers and falls back
 *      to a server-side IP lookup — see app/api/geo/route.ts)
 *   4. all rungs dry → null, and the chip keeps its static state
 *
 * Precision ranking (precise > ip) means a coarser fix can paint first and a
 * later precise fix silently re-resolves — consumers fade-swap the label
 * (keyed span / CSS animation), never reflow. A worse-ranked late arrival
 * never downgrades an applied better fix.
 *
 * The facility collection is fetched once per page (module-level promise) and
 * shared by every consumer. Both client fetches are SAME-ORIGIN and go through
 * smileApiPath(), which prefixes the "/bb" mount segment the gateway routes to
 * this pod (/bb/api/...). A bare root-relative /api/... resolves against the
 * origin root, which the gateway does not route here, so it 404'd on staging
 * (TAG-176 /api/geo/, TAG-177 /api/facility/collection/). We deliberately do
 * NOT use config.baseUrl's fully qualified URL, which would send the request
 * off the current preview/stage door under ENVIRONMENT_NAME=stage — see
 * smileApiPath.ts. Nearest selection stays getClosestFacility (haversine)
 * exactly as shipped.
 */

export const STATIC_LABEL = "Find an office";
export const LOCATION_HREF = "/smile/find-an-office/";
/**
 * The six-field `nearest-office.v1` shape of the collection, not the full
 * 900 KB: this hook reads code, name, slug, address {city, stateCode, zipCode}
 * and location {latitude, longitude}, nothing else (perf program 2026-09-10,
 * ranked cause 4). The shape is versioned in the path; see
 * lib/utils/facility-collection-shape.ts and app/api/facility/collection/.
 */
const FACILITY_COLLECTION_PATH = smileApiPath("/api/facility/collection/nearest-office-v1/");
const GEO_ENDPOINT = smileApiPath("/api/geo/");
const CACHE_KEY = "smile:last-known-geo:v1";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export type NearestOffice = {
  label: string;
  href: string;
  /**
   * The facility code of the office the chip names, so the Smile Header
   * atom's Book pin (bookOfficePin "chip", D9 G5) can carry the SAME office
   * the chip shows. Absent when the collection row carries no code.
   */
  code?: string;
};

type GeoSource = "precise" | "ip";

type GeoFix = {
  latitude: number;
  longitude: number;
  source: GeoSource;
};

// precise (user-granted GPS) outranks ip (city-level). Applied rank is
// monotonic: equal rank re-applies (fresher fix), lower rank is dropped.
const SOURCE_RANK: Record<GeoSource, number> = { ip: 1, precise: 2 };

/**
 * Builds the office-page href for a BEFFE facility slug ("/pa/homestead/240-
 * waterfront-dr-e" → "/dentist/pa/homestead/240-waterfront-dr-e/"). The ODP
 * lives under app/dentist/[state]/[city]/, so the office page is the slug
 * mounted under /dentist with a trailing slash (the bare path 308-redirects).
 * Anything that does not look like a rooted path falls back to the locator.
 */
export function officeHref(slug: unknown): string {
  if (typeof slug === "string" && slug.startsWith("/") && !/\s/.test(slug)) {
    return `/dentist${slug.replace(/\/+$/, "")}/`;
  }
  return LOCATION_HREF;
}

/**
 * Resolves the nearest-office chip for the given coordinates, or null when it
 * can't be determined (empty collection, no geocoded facilities, or the
 * closest facility lacks displayable fields) — null means keep the static
 * chip. Label format matches the Figma northstar strip (vy1bHbX6sIjly3EzwAQA62
 * node 45:4411, verified live 8/24): "Homestead, PA 15120 · 5.5 mi" —
 * NOT the earlier "Your office: City, ST" draft copy. Exported for unit
 * coverage of the selection/formatting/href logic.
 */
export function nearestOffice(
  facilities: NearestOfficeCollectionItem[],
  latitude: number,
  longitude: number,
): NearestOffice | null {
  if (!Array.isArray(facilities) || facilities.length === 0) return null;

  const closest = getClosestFacility(facilities, latitude, longitude);
  if (!closest) return null;

  const facilityLat = closest.location?.latitude;
  const facilityLng = closest.location?.longitude;
  const miles =
    typeof facilityLat === "number" && typeof facilityLng === "number"
      ? calculateDistance(latitude, longitude, facilityLat, facilityLng)
      : null;
  const distanceSuffix = miles === null ? "" : ` · ${miles.toFixed(1)} mi`;
  const href = officeHref((closest as { slug?: unknown }).slug);
  const code =
    typeof closest.code === "string" && closest.code.trim() ? { code: closest.code } : {};

  const { city, stateCode, zipCode } = closest.address ?? {};
  if (city && stateCode && zipCode) {
    return { label: `${city}, ${stateCode} ${zipCode}${distanceSuffix}`, href, ...code };
  }
  if (city && stateCode) {
    return { label: `${city}, ${stateCode}${distanceSuffix}`, href, ...code };
  }
  if (closest.name) {
    return { label: `${closest.name}${distanceSuffix}`, href, ...code };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Rung 1: last-known fix (localStorage). Storage can be unavailable (private
// mode, blocked third-party context) — every touch is try/catch fail-soft.

function readCachedFix(): GeoFix | null {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GeoFix> & { ts?: number };
    if (
      typeof parsed.latitude !== "number" ||
      !Number.isFinite(parsed.latitude) ||
      typeof parsed.longitude !== "number" ||
      !Number.isFinite(parsed.longitude) ||
      (parsed.source !== "precise" && parsed.source !== "ip") ||
      typeof parsed.ts !== "number" ||
      Date.now() - parsed.ts > CACHE_TTL_MS
    ) {
      return null;
    }
    return {
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      source: parsed.source,
    };
  } catch {
    return null;
  }
}

function writeCachedFix(fix: GeoFix): void {
  try {
    globalThis.localStorage?.setItem(CACHE_KEY, JSON.stringify({ ...fix, ts: Date.now() }));
  } catch {
    // Storage unavailable: the ladder still works, just without rung 1.
  }
}

// ---------------------------------------------------------------------------
// Rung 2: precise geolocation, but ONLY when already granted. permissions
// .query is the no-prompt probe; anywhere it is missing (old Safari) or
// throws, we treat the state as not-granted and stay prompt-free.

async function isGeolocationGranted(): Promise<boolean> {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions || !navigator.geolocation) {
      return false;
    }
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state === "granted";
  } catch {
    return false;
  }
}

function getPrecisePosition(): Promise<GeoFix | null> {
  return new Promise((resolve) => {
    try {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source: "precise",
          }),
        () => resolve(null),
        { maximumAge: 300_000, timeout: 8_000 },
      );
    } catch {
      resolve(null);
    }
  });
}

// ---------------------------------------------------------------------------
// Rung 3: IP-based approximate fix from the door's own /api/geo/ route.
// 204/failure/malformed payload all resolve null (fail-soft, rung 4).
// Memoized module-level like the facility fetch so every hook consumer
// shares ONE request per page; a miss clears the memo so a later mount can
// retry.

let ipFixPromise: Promise<GeoFix | null> | null = null;

function getIpFix(): Promise<GeoFix | null> {
  if (!ipFixPromise) {
    ipFixPromise = (async (): Promise<GeoFix | null> => {
      try {
        const res = await fetch(GEO_ENDPOINT);
        if (!res.ok || res.status === 204) return null;
        const data = (await res.json()) as {
          latitude?: unknown;
          longitude?: unknown;
        };
        if (
          typeof data.latitude === "number" &&
          Number.isFinite(data.latitude) &&
          typeof data.longitude === "number" &&
          Number.isFinite(data.longitude)
        ) {
          return { latitude: data.latitude, longitude: data.longitude, source: "ip" };
        }
        return null;
      } catch {
        return null;
      }
    })().then((fix) => {
      if (fix === null) ipFixPromise = null;
      return fix;
    });
  }
  return ipFixPromise;
}

// ---------------------------------------------------------------------------
// Shared facility fetch: one request per page load for every hook consumer.
// A failed fetch clears the memo so a later consumer can retry.

let facilitiesPromise: Promise<NearestOfficeCollectionItem[]> | null = null;

function fetchFacilities(): Promise<NearestOfficeCollectionItem[]> {
  if (!facilitiesPromise) {
    // Low fetch priority: every /smile view asks for the collection, so at the
    // default (High) it shares the phone's link with the hero image inside the
    // LCP window (perf audit 2026-09-08, P-01). The chip is progressive
    // enhancement and can wait behind the hero. (The body is now the ~215 KB
    // nearest-office shape, gzip-compressed and browser-cacheable - see the
    // route - but it still has no business ahead of the hero.)
    facilitiesPromise = fetch(FACILITY_COLLECTION_PATH, { priority: "low" })
      .then((res) => {
        if (!res.ok) throw new Error(`facility collection ${res.status}`);
        return res.json() as Promise<NearestOfficeCollectionItem[]>;
      })
      .catch((error) => {
        facilitiesPromise = null;
        throw error;
      });
  }
  return facilitiesPromise;
}

/** Test-only: drop the shared memos between vitest cases. */
export function __resetNearestOfficeSharedStateForTests(): void {
  facilitiesPromise = null;
  ipFixPromise = null;
  try {
    globalThis.localStorage?.removeItem(CACHE_KEY);
  } catch {
    // storage unavailable — nothing memoized there anyway
  }
}

type UseNearestOfficeOptions = {
  /**
   * Set false to keep the ladder fully inert — no localStorage read, no
   * fetch, no geolocation permission probe. Defaults true so every existing
   * consumer (SmileNearestOfficeEnhancer, etc.) is byte-unchanged. Pass
   * `{ enabled: !isEditorMode }` from the Studio canvas: the effect returns
   * BEFORE touching any of the ladder's rungs.
   */
  enabled?: boolean;
};

/**
 * The shared hook. Returns the resolved nearest office, or null while (or
 * for as long as) nothing on the ladder produced a fix — consumers render
 * their own static fallback for null. Pass `{ enabled: false }` to keep the
 * ladder fully inert (e.g. the Studio editor canvas).
 */
export function useNearestOffice(options?: UseNearestOfficeOptions): NearestOffice | null {
  const enabled = options?.enabled ?? true;
  const [office, setOffice] = useState<NearestOffice | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let appliedRank = 0;

    const apply = async (fix: GeoFix, fromCache: boolean): Promise<void> => {
      try {
        if (SOURCE_RANK[fix.source] < appliedRank) return;
        const facilities = await fetchFacilities();
        if (cancelled || SOURCE_RANK[fix.source] < appliedRank) return;
        const next = nearestOffice(facilities, fix.latitude, fix.longitude);
        if (!next || cancelled) return;
        appliedRank = SOURCE_RANK[fix.source];
        setOffice(next);
        if (!fromCache) writeCachedFix(fix);
      } catch {
        // Degrade silently: the static state is the designed fallback.
      }
    };

    const start = () => {
      if (cancelled) return;

      // Rung 1: paint from the last-known fix immediately (if any).
      const cached = readCachedFix();
      if (cached) void apply(cached, true);

      // Rungs 2→3, sequential: precise only when already granted (never
      // prompt from chrome); otherwise — or when the precise read fails —
      // the permission-free IP fix.
      void (async () => {
        if (await isGeolocationGranted()) {
          const precise = cancelled ? null : await getPrecisePosition();
          if (precise && !cancelled) {
            await apply(precise, false);
            return;
          }
        }
        if (cancelled) return;
        const ipFix = await getIpFix();
        if (ipFix && !cancelled) await apply(ipFix, false);
      })();
    };

    // The ladder's first network leg is the facility collection, which
    // otherwise starts at hydration and competes with the hero image for
    // the LCP window on phones (perf audit 2026-09-08, P-01). Wait for the
    // window load event; a page that has already loaded (client navigation,
    // late hydration) starts on the spot. useGeoCoords below is untouched:
    // the scheduler's step 1 needs its fix as early as it can get it.
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });

    return () => {
      cancelled = true;
      window.removeEventListener("load", start);
    };
  }, [enabled]);

  return office;
}

type GeoCoords = {
  latitude: number;
  longitude: number;
  source: GeoSource;
};

/**
 * The bare coordinate resolver behind useNearestOffice: the SAME no-prompt
 * ladder (cached fix -> precise GPS only when already granted -> the
 * permission-free IP fix), but it returns the raw fix instead of a resolved
 * office. Consumers that need the coordinates themselves - e.g. the in-page
 * three-step scheduler, which resolves its OWN bookable office set from them -
 * use this; the header chip uses useNearestOffice. Returns null while (or for
 * as long as) nothing on the ladder produced a fix. Precision is monotonic: a
 * coarser fix can paint first and a later precise fix silently replaces it; a
 * worse-ranked late arrival never downgrades a better applied fix. Pass
 * enabled=false to stay inert (e.g. the Studio editor canvas).
 */
export function useGeoCoords(enabled = true): GeoCoords | null {
  const [coords, setCoords] = useState<GeoCoords | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let appliedRank = 0;

    const apply = (fix: GeoFix, fromCache: boolean): void => {
      if (cancelled || SOURCE_RANK[fix.source] < appliedRank) return;
      appliedRank = SOURCE_RANK[fix.source];
      setCoords({ latitude: fix.latitude, longitude: fix.longitude, source: fix.source });
      if (!fromCache) writeCachedFix(fix);
    };

    // Rung 1: last-known fix immediately (if any).
    const cached = readCachedFix();
    if (cached) apply(cached, true);

    // Rungs 2->3: precise only when already granted (never prompt);
    // otherwise - or when the precise read fails - the IP fix.
    void (async () => {
      if (await isGeolocationGranted()) {
        const precise = cancelled ? null : await getPrecisePosition();
        if (precise && !cancelled) {
          apply(precise, false);
          return;
        }
      }
      if (cancelled) return;
      const ipFix = await getIpFix();
      if (ipFix && !cancelled) apply(ipFix, false);
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return coords;
}
